import { closestCenter, DndContext, KeyboardSensor, PointerSensor, useSensor, useSensors, type Announcements, type DragEndEvent } from "@dnd-kit/core";
import { arrayMove, SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import type { ReactNode } from "react";
import { cn } from "@/lib/cn";
import { Button } from "./Button";
import { Icono } from "./Icono";

export interface ElementoOrdenable {
  id: number;
  /** Texto para lectores de pantalla y los botones ("Subir «texto»"). */
  texto: string;
  /** Lo que se ve (por defecto, `texto`). */
  contenido?: ReactNode;
}

export interface ListaOrdenableProps {
  etiqueta: string;
  elementos: ElementoOrdenable[];
  /** Los ids en el orden nuevo (la lista entera). */
  onMover: (ids: number[]) => void;
  deshabilitado?: boolean;
  className?: string;
}

function Fila({ e, pos, total, mover, deshabilitado }: { e: ElementoOrdenable; pos: number; total: number; mover: (desde: number, hasta: number) => void; deshabilitado?: boolean }) {
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } = useSortable({ id: e.id, disabled: deshabilitado });
  return (
    <li
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={cn("flex items-center gap-2 bg-superficie px-2 py-2", isDragging && "relative z-10 rounded-control shadow-flotante")}
    >
      <button
        type="button"
        ref={setActivatorNodeRef}
        {...attributes}
        {...listeners}
        aria-label={`Arrastrar ${e.texto}`}
        disabled={deshabilitado}
        className="grid size-11 shrink-0 cursor-grab touch-none place-items-center rounded-control text-texto-suave hover:bg-superficie-2 active:cursor-grabbing disabled:cursor-not-allowed"
      >
        <Icono nombre="arrastrar" className="size-5" />
      </button>
      <div className="min-w-0 flex-1">{e.contenido ?? e.texto}</div>
      <Button variante="fantasma" tamanio="sm" aria-label={`Subir ${e.texto}`} disabled={deshabilitado || pos === 0} onClick={() => mover(pos, pos - 1)}>
        <Icono nombre="flecha" className="size-4 -rotate-90" />
      </Button>
      <Button variante="fantasma" tamanio="sm" aria-label={`Bajar ${e.texto}`} disabled={deshabilitado || pos === total - 1} onClick={() => mover(pos, pos + 1)}>
        <Icono nombre="flecha" className="size-4 rotate-90" />
      </Button>
    </li>
  );
}

/**
 * Lista que se reordena arrastrando (mouse, dedo o teclado: Espacio para tomar, flechas, Espacio para soltar) o con
 * los botones subir/bajar. Controlada: `onMover` recibe los ids en el orden nuevo y quien la usa guarda y pasa los
 * `elementos` en ese orden. @dnd-kit.
 */
export function ListaOrdenable({ etiqueta, elementos, onMover, deshabilitado, className }: ListaOrdenableProps) {
  const sensores = useSensors(useSensor(PointerSensor), useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }));
  const ids = elementos.map((e) => e.id);
  const texto = (id: string | number) => elementos.find((e) => e.id === id)?.texto ?? "";
  const lugar = (id: string | number | undefined) => (id === undefined ? "" : ` (lugar ${ids.indexOf(Number(id)) + 1} de ${ids.length})`);
  const anuncios: Announcements = {
    onDragStart: ({ active }) => `Tomaste ${texto(active.id)}${lugar(active.id)}.`,
    onDragOver: ({ active, over }) => (over ? `${texto(active.id)} va al${lugar(over.id)}.` : `${texto(active.id)} está fuera de la lista.`),
    onDragEnd: ({ active, over }) => (over ? `Soltaste ${texto(active.id)}${lugar(over.id)}.` : `Soltaste ${texto(active.id)}.`),
    onDragCancel: ({ active }) => `Cancelado: ${texto(active.id)} quedó donde estaba.`,
  };

  const mover = (desde: number, hasta: number) => {
    if (hasta < 0 || hasta >= ids.length || desde === hasta) return;
    onMover(arrayMove(ids, desde, hasta));
  };
  const alSoltar = ({ active, over }: DragEndEvent) => {
    if (over) mover(ids.indexOf(Number(active.id)), ids.indexOf(Number(over.id)));
  };

  return (
    <DndContext
      sensors={sensores}
      collisionDetection={closestCenter}
      onDragEnd={alSoltar}
      accessibility={{
        announcements: anuncios,
        screenReaderInstructions: { draggable: "Para mover, apretá Espacio, usá las flechas arriba y abajo, y Espacio para soltar o Escape para cancelar." },
      }}
    >
      <SortableContext items={ids} strategy={verticalListSortingStrategy}>
        <ol aria-label={etiqueta} className={cn("divide-y divide-borde rounded-control border border-borde", className)}>
          {elementos.map((e, pos) => (
            <Fila key={e.id} e={e} pos={pos} total={elementos.length} mover={mover} deshabilitado={deshabilitado} />
          ))}
        </ol>
      </SortableContext>
    </DndContext>
  );
}
